import React, { useCallback, useState } from 'react';
import { Button, DatePicker, Form, InputNumber, Space, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import ResponsiveModal from '../../../Shared/ResponsiveModal/ResponsiveModal';
import { createHistoricalImport } from '../../ChannelsServices';
import { useErrorHandler } from '../../../../Hooks/useErrorHandler';

/**
 * HistoricalImportModal component for creating a new historical import.
 * Reusable modal used in ChannelDetail and ConversationsHeader.
 *
 * @param {Object} props
 * @param {boolean} props.open - Modal visibility state
 * @param {string} props.channelId - ID of the channel to import history for
 * @param {Function} props.onCancel - Callback when modal is closed
 * @param {Function} [props.onSuccess] - Optional callback when import successfully starts
 */
const HistoricalImportModal = ({ open, channelId, onCancel, onSuccess }) => {
    const { t } = useTranslation();
    const { handleError } = useErrorHandler();
    const [form] = Form.useForm();
    const [importing, setImporting] = useState(false);

    // Reset form when modal opens
    React.useEffect(() => {
        if (open) {
            form.resetFields();
        }
    }, [open, form]);

    const handleFormSubmit = useCallback(async (values) => {
        setImporting(true);
        try {
            const body = {};
            if (values.oldest_date) {
                body.oldest_date = values.oldest_date.toISOString();
            }
            if (values.max_conversations != null) {
                body.max_conversations = values.max_conversations;
            }
            if (values.max_messages_per_conversation != null) {
                body.max_messages_per_conversation = values.max_messages_per_conversation;
            }
            
            const newImport = await createHistoricalImport(channelId, body);
            message.success(t('import_started_successfully'));
            
            if (onSuccess) {
                onSuccess(newImport?.data || newImport);
            }
            onCancel();
        } catch (error) {
            handleError(error);
        } finally {
            setImporting(false);
        }
    }, [channelId, t, handleError, onSuccess, onCancel]);

    return (
        <ResponsiveModal
            title={t('start_historical_import')}
            open={open}
            onCancel={onCancel}
            desktopWidth={480}
            footer={
                <Space>
                    <Button onClick={onCancel} disabled={importing}>{t('cancel')}</Button>
                    <Button
                        type="primary"
                        loading={importing}
                        onClick={() => form.submit()}
                    >
                        {importing ? t('starting') : t('start_import')}
                    </Button>
                </Space>
            }
        >
            <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
                <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
                    {t('historical_import_date_description')}
                </Typography.Paragraph>
                <Form.Item
                    name="oldest_date"
                    label={t('start_date')}
                    tooltip={t('start_date_tooltip')}
                >
                    <DatePicker
                        showTime
                        style={{ width: '100%' }}
                        disabledDate={(current) => current && current > new Date()}
                        placeholder={t('select_start_date')}
                    />
                </Form.Item>
                <Form.Item
                    name="max_conversations"
                    label={t('max_conversations')}
                    tooltip={t('max_conversations_tooltip')}
                >
                    <InputNumber
                        style={{ width: '100%' }}
                        min={1}
                        max={10000}
                        placeholder="100"
                    />
                </Form.Item>
                <Form.Item
                    name="max_messages_per_conversation"
                    label={t('max_messages_per_conversation')}
                    tooltip={t('max_messages_per_conversation_tooltip')}
                >
                    <InputNumber
                        style={{ width: '100%' }}
                        min={1}
                        max={500}
                        placeholder="20"
                    />
                </Form.Item>
            </Form>
        </ResponsiveModal>
    );
};

export default HistoricalImportModal;
